import { msg } from '@lingui/core/macro';
import { showToast } from 'state/toast';
import { user } from 'utils/auth/user';
import type { SessionState } from './computed';

/** Stable id so the visible-toast dedup (state/toast.ts) targets this exact notification */
export const OUTDATED_VEHICLE_TOAST_ID = 'INFO_OUTDATED_VEHICLE_3D_BLOCKED';

/**
 * AVS-4270: enforcement for outdated (3+ years) vehicles served for 2D-only display.
 *
 * - LEVEL-TRIGGERED 2D forcing: on every session change, while the active vehicle is
 *   3D-blocked (server-set `carDetails.outdated3D` + tenant flag — see
 *   `SessionState.is3DBlocked`) the experience is forced back to '2D'. Subscribing to
 *   state changes covers ALL activation paths (showCar/addVehicle, activateVehicle,
 *   swapComparedVehicles, session sync). Loop-safe: `State.setValues` no-ops on
 *   unchanged values (utils/state.ts:146) and the re-entrant change sees
 *   activeExperience already '2D'. Deliberately NOT role-gated: every client enforces
 *   the block.
 * - EDGE-TRIGGERED toast, once per blocked-vehicle activation (plan R4): fires when the
 *   blocked-vehicle identity differs from the last-toasted one. The tracker clears when
 *   a non-blocked vehicle becomes active (returning to the old car re-toasts) and never
 *   re-fires on unrelated changes, so a dismissed toast stays dismissed. The identity is
 *   keyed on prString first: endSession() clears activeVehicleId while keeping
 *   carDetails/prString, and a vehicle-id-first key flips representation on teardown,
 *   falsely re-toasting when a call ends.
 *
 * Toast is DEALER-ONLY per PO decision 5: this subscriber runs on the shared session
 * state for EVERY participant — customers receive the same setValues via session sync
 * (localSync / rtc SessionUpdated) — so the dealer-role gate (carImages.ts precedent),
 * not the activation source, is what keeps the toast dealer-side.
 */
export function initOutdatedVehicleEnforcement(state: SessionState) {
  // Last blocked-vehicle identity we toasted for — purely vehicle-keyed, so session
  // lifecycle changes (end call, id renewal) can never re-fire for the same car
  let lastToastedVehicleIdentity: string | undefined;

  state.on('change', () => {
    if (!state.is3DBlocked) {
      // Leaving the blocked vehicle re-arms the toast for its next activation (R4)
      lastToastedVehicleIdentity = undefined;
      return;
    }

    // Edge-triggered toast: once per blocked-vehicle activation, dealer-only (see above)
    if (user.type === 'dealer') {
      const identity = state.get('prString') || state.get('activeVehicleId');
      if (identity && identity !== lastToastedVehicleIdentity) {
        lastToastedVehicleIdentity = identity;
        showToast({
          // Exact copy per AVS-4270 acceptance criteria
          title: msg`This car is older than 3 years and cannot be visualised in 3D`,
          type: 'info',
          notificationId: OUTDATED_VEHICLE_TOAST_ID,
        });
      }
    }

    // Level-triggered forcing: a blocked vehicle can never stay in 3D
    if (state.get('activeExperience') !== '2D') {
      state.setValues({ activeExperience: '2D' });
    }
  });
}
